import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class TopupTemplateService {
  static collectionName = 'topupTemplates';

  static TEMPLATE_FAMILIES = {
    GAMING: 'gaming',
    SOCIAL: 'social',
    SUBSCRIPTION: 'subscription',
    MANUAL: 'manual',
    ADVANCED: 'advanced'
  };

  static FIELD_TYPES = {
    TEXT: 'text',
    TEXTAREA: 'textarea',
    PHONE: 'phone',
    URL: 'url',
    EMAIL: 'email',
    NUMBER: 'number',
    SELECT: 'select',
    INSTRUCTION: 'instruction'
  };

  static SUBMISSION_MODES = {
    WHATSAPP: 'manual_whatsapp',
    DASHBOARD: 'manual_dashboard',
    EXTERNAL: 'external_supplier'
  };

  static normalizeFamily(family) {
    const allowedFamilies = Object.values(this.TEMPLATE_FAMILIES);
    return allowedFamilies.includes(family) ? family : this.TEMPLATE_FAMILIES.ADVANCED;
  }

  static normalizeSubmissionMode(mode) {
    const allowedModes = Object.values(this.SUBMISSION_MODES);
    return allowedModes.includes(mode) ? mode : this.SUBMISSION_MODES.DASHBOARD;
  }

  static normalizeField(field = {}, index = 0) {
    const type = Object.values(this.FIELD_TYPES).includes(field.type)
      ? field.type
      : this.FIELD_TYPES.TEXT;

    return {
      id: field.id || `field_${Date.now()}_${index}`,
      label: field.label || '',
      type,
      required: type === this.FIELD_TYPES.INSTRUCTION ? false : Boolean(field.required),
      placeholder: type === this.FIELD_TYPES.INSTRUCTION ? '' : (field.placeholder || ''),
      helpText: field.helpText || '',
      options: Array.isArray(field.options)
        ? field.options.filter(Boolean)
        : []
    };
  }

  static normalizeTemplateDocument(docSnapshot) {
    const data = docSnapshot.data() || {};

    return {
      id: docSnapshot.id,
      ...data,
      family: this.normalizeFamily(data.family),
      submissionMode: this.normalizeSubmissionMode(data.submissionMode),
      fields: Array.isArray(data.fields)
        ? data.fields.map((field, index) => this.normalizeField(field, index))
        : []
    };
  }

  static buildTemplatePayload(templateData = {}) {
    return {
      name: String(templateData.name || '').trim(),
      family: this.normalizeFamily(templateData.family),
      description: String(templateData.description || '').trim(),
      status: templateData.status === 'inactive' ? 'inactive' : 'active',
      submissionMode: this.normalizeSubmissionMode(templateData.submissionMode),
      whatsappNumber: String(templateData.whatsappNumber || '').trim(),
      fields: Array.isArray(templateData.fields)
        ? templateData.fields.map((field, index) => this.normalizeField(field, index))
        : []
    };
  }

  static async getAllTemplates() {
    try {
      const templatesQuery = query(
        collection(db, this.collectionName),
        orderBy('name')
      );

      const snapshot = await getDocs(templatesQuery);
      return snapshot.docs.map((docSnapshot) => this.normalizeTemplateDocument(docSnapshot));
    } catch (error) {
      console.error('Erreur lors de la récupération des templates Topup:', error);
      throw error;
    }
  }

  static async getActiveTemplates() {
    try {
      const templatesQuery = query(
        collection(db, this.collectionName),
        where('status', '==', 'active'),
        orderBy('name')
      );

      const snapshot = await getDocs(templatesQuery);
      return snapshot.docs.map((docSnapshot) => this.normalizeTemplateDocument(docSnapshot));
    } catch (error) {
      console.error('Erreur lors de la récupération des templates actifs:', error);
      throw error;
    }
  }

  static async getTemplateById(templateId) {
    try {
      const templateRef = doc(db, this.collectionName, templateId);
      const templateDoc = await getDoc(templateRef);

      if (!templateDoc.exists()) {
        return null;
      }

      return this.normalizeTemplateDocument(templateDoc);
    } catch (error) {
      console.error('Erreur lors de la récupération du template Topup:', error);
      throw error;
    }
  }

  static async createTemplate(templateData) {
    try {
      const payload = this.buildTemplatePayload(templateData);
      const docRef = await addDoc(collection(db, this.collectionName), {
        ...payload,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création du template Topup:', error);
      throw error;
    }
  }

  static async updateTemplate(templateId, templateData) {
    try {
      const payload = this.buildTemplatePayload(templateData);
      const templateRef = doc(db, this.collectionName, templateId);
      await updateDoc(templateRef, {
        ...payload,
        updatedAt: serverTimestamp()
      });

      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour du template Topup:', error);
      throw error;
    }
  }

  static async deleteTemplate(templateId) {
    try {
      await deleteDoc(doc(db, this.collectionName, templateId));
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression du template Topup:', error);
      throw error;
    }
  }

  static async toggleTemplateStatus(templateId) {
    try {
      const template = await this.getTemplateById(templateId);
      if (!template) {
        throw new Error('Template introuvable');
      }

      const nextStatus = template.status === 'active' ? 'inactive' : 'active';
      await updateDoc(doc(db, this.collectionName, templateId), {
        status: nextStatus,
        updatedAt: serverTimestamp()
      });

      return nextStatus;
    } catch (error) {
      console.error('Erreur lors du changement de statut du template Topup:', error);
      throw error;
    }
  }

  static async templateExistsByName(name, excludeId = null) {
    try {
      const normalizedName = String(name || '').trim().toLowerCase();
      const templates = await this.getAllTemplates();

      return templates.some((template) => {
        if (excludeId && template.id === excludeId) {
          return false;
        }

        return String(template.name || '').trim().toLowerCase() === normalizedName;
      });
    } catch (error) {
      console.error('Erreur lors de la vérification du template Topup:', error);
      throw error;
    }
  }

  static getFamilyLabel(family) {
    const normalizedFamily = this.normalizeFamily(family);

    if (normalizedFamily === this.TEMPLATE_FAMILIES.GAMING) return 'Gaming';
    if (normalizedFamily === this.TEMPLATE_FAMILIES.SOCIAL) return 'Reseaux sociaux';
    if (normalizedFamily === this.TEMPLATE_FAMILIES.SUBSCRIPTION) return 'Abonnements';
    if (normalizedFamily === this.TEMPLATE_FAMILIES.MANUAL) return 'WhatsApp / Manuel';
    return 'Avance';
  }

  static getSubmissionModeLabel(mode) {
    const normalizedMode = this.normalizeSubmissionMode(mode);

    if (normalizedMode === this.SUBMISSION_MODES.WHATSAPP) return 'WhatsApp';
    if (normalizedMode === this.SUBMISSION_MODES.EXTERNAL) return 'Fournisseur externe';
    return 'Dashboard admin';
  }

  static getFieldTypeLabel(type) {
    if (type === this.FIELD_TYPES.INSTRUCTION) return 'Instruction';
    if (type === this.FIELD_TYPES.TEXTAREA) return 'Texte long';
    if (type === this.FIELD_TYPES.PHONE) return 'Telephone';
    if (type === this.FIELD_TYPES.URL) return 'Lien';
    if (type === this.FIELD_TYPES.EMAIL) return 'Email';
    if (type === this.FIELD_TYPES.NUMBER) return 'Nombre';
    if (type === this.FIELD_TYPES.SELECT) return 'Liste';
    return 'Texte';
  }
}
